import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios, { API_URL } from '../api/axiosConfig';
import { AuthContext } from '../context/AuthContext';
import * as XLSX from 'xlsx';
import Navbar from '../components/Navbar';
import Sidebar from '../components/Sidebar';
import CargarPagoImpuestoModal from '../components/CargarPagoImpuestoModal';
import './Dashboard.css';

const Impuestos = () => {
  const { user } = useContext(AuthContext);
  const isSuperUser = user?.role === 'superadmin' || user?.profile_name === 'superadmin' || user?.username === 'laura.avila' || user?.profile_name === 'auxiliar transiapp';
  const isSuperAdmin = user?.role === 'superadmin' || user?.profile_name === 'superadmin';
  const canManageImpuestos = isSuperUser || user?.profile_name !== 'operador';
  const navigate = useNavigate();

  const handleDeleteSoporte = async (tramiteId, tipoDocumento, label) => {
    if (!window.confirm(`¿Estás seguro de eliminar el soporte de ${label} del trámite #${tramiteId}? Esta acción eliminará el archivo del sistema.`)) {
      return;
    }
    try {
      setLoadingTramites(true);
      const query = tipoDocumento ? `?tipo_documento=${tipoDocumento}` : '';
      const res = await axios.delete(`/api/impuestos/${tramiteId}/soportes${query}`);
      alert(res.data.message || 'Soporte eliminado correctamente');
      await fetchStats();
      loadTramites();
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.message || 'Error al eliminar el soporte');
      setLoadingTramites(false);
    }
  };

  const [stats, setStats] = useState([]);
  const [loadingStats, setLoadingStats] = useState(true);
  const [error, setError] = useState('');
  
  const [tramites, setTramites] = useState([]);
  const [loadingTramites, setLoadingTramites] = useState(false);
  
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [tramiteToPay, setTramiteToPay] = useState(null);
  
  // Filters
  const [filterConcesionario, setFilterConcesionario] = useState('todos');
  const [filterEstado, setFilterEstado] = useState('todos'); // Impuestos status
  const [filterEstadoTramite, setFilterEstadoTramite] = useState('todos'); // Estado del trámite status
  const [search, setSearch] = useState('');
  const [fechaInicio, setFechaInicio] = useState('');
  const [fechaFin, setFechaFin] = useState('');
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  
  const [selectedTramites, setSelectedTramites] = useState([]);

  useEffect(() => {
    if (user?.role !== 'superadmin' && user?.profile_name !== 'superadmin' && user?.profile_name !== 'operador' && user?.profile_name !== 'auxiliar transiapp') {
      navigate('/dashboard');
    }
    fetchStats();
  }, [user, navigate]);

  const fetchStats = async () => {
    try {
      setLoadingStats(true);
      const res = await axios.get('/api/impuestos/stats');
      setStats(res.data);
      setError('');
    } catch (err) {
      setError('Error al cargar estadísticas de impuestos');
      console.error(err);
    } finally {
      setLoadingStats(false);
    }
  };

  const loadTramites = async () => {
    try {
      setLoadingTramites(true);
      const queryParams = new URLSearchParams({
        concesionario_id: filterConcesionario,
        estado: filterEstado,
        estado_tramite: filterEstadoTramite,
        search,
        fecha_inicio: fechaInicio,
        fecha_fin: fechaFin
      }).toString();
      
      const res = await axios.get(`/api/impuestos/tramites?${queryParams}`);
      setTramites(res.data);
      setSelectedTramites([]); // Clear selection when changing filter or loading
      setCurrentPage(1); // Reset to first page
    } catch (err) {
      console.error(err);
      alert('Error al cargar los trámites');
    } finally {
      setLoadingTramites(false);
    }
  };

  useEffect(() => {
    loadTramites();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterConcesionario, filterEstado, filterEstadoTramite, search, fechaInicio, fechaFin]);

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedTramites(tramites.map(t => t.id));
    } else {
      setSelectedTramites([]);
    }
  };

  const handleSelectTramite = (id) => {
    if (selectedTramites.includes(id)) {
      setSelectedTramites(selectedTramites.filter(tId => tId !== id));
    } else {
      setSelectedTramites([...selectedTramites, id]);
    }
  };

  const handleUpdateStatus = async (nuevoEstado) => {
    if (selectedTramites.length === 0) {
      alert('Por favor selecciona al menos un trámite');
      return;
    }
    
    if (!window.confirm(`¿Estás seguro de marcar los ${selectedTramites.length} trámites seleccionados como ${nuevoEstado.replace('_', ' ').toUpperCase()}?`)) {
      return;
    }
    
    try {
      setLoadingTramites(true);
      const res = await axios.put('/api/impuestos/update-status', {
        tramite_ids: selectedTramites,
        nuevo_estado: nuevoEstado
      });
      alert(res.data.message || `Trámites marcados como ${nuevoEstado.replace('_', ' ')} exitosamente.`);
      setSelectedTramites([]);
      await fetchStats();
      loadTramites();
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.message || 'Error al actualizar el estado de los impuestos');
      setLoadingTramites(false);
    }
  };

  const exportToExcel = () => {
    if (tramites.length === 0) {
      alert('No hay datos para exportar con los filtros actuales.');
      return;
    }

    const exportData = tramites.map(t => ({
      'ID Trámite': t.id,
      'Placa': t.plate || 'Sin Placa',
      'Cilindraje': t.cilindraje || 'N/A',
      'Tipo de Trámite': t.tipo_tramite,
      'Nombre del propietario': t.propietario || 'N/A',
      'ID propietario': t.documento_propietario || 'N/A',
      'Organismo de Tránsito': t.transito_name || 'N/A',
      'Usuario Creador': t.usuario_creador,
      'Estado del Trámite': t.estado_tramite,
      'Concesionario': t.concesionario_name || 'N/A',
      'Punto de Venta': t.punto_venta_name || 'N/A',
      'Zona': t.zona_name || 'N/A',
      'Estado Impuesto': t.impuesto_estado.replace('_', ' ').toUpperCase(),
      'Valor pagado del impuesto': Number(t.impuesto_valor || 0),
      'Fecha Creación': new Date(t.created_at).toLocaleString(),
      'Fecha Registro': t.impuesto_fecha_registro ? new Date(t.impuesto_fecha_registro).toLocaleString() : 'N/A',
      'Fecha Cobro': t.impuesto_fecha_cobro ? new Date(t.impuesto_fecha_cobro).toLocaleString() : 'N/A',
      'Fecha Pago': t.impuesto_fecha_pago ? new Date(t.impuesto_fecha_pago).toLocaleString() : 'N/A',
      'Fecha Recaudo': t.impuesto_fecha_recaudo ? new Date(t.impuesto_fecha_recaudo).toLocaleString() : 'N/A'
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    // El valor va como número (se puede sumar en Excel) con formato de moneda
    const colValor = Object.keys(exportData[0] || {}).indexOf('Valor pagado del impuesto');
    if (colValor >= 0) {
      for (let r = 1; r <= exportData.length; r++) {
        const cell = worksheet[XLSX.utils.encode_cell({ r, c: colValor })];
        if (cell) { cell.t = 'n'; cell.z = '"$" #,##0'; }
      }
    }
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Impuestos');
    
    const concName = filterConcesionario === 'todos' ? 'Todos' : (stats.find(s => s.concesionario_id.toString() === filterConcesionario)?.concesionario_name || 'Filtro');
    XLSX.writeFile(workbook, `Impuestos_${concName.replace(/[^a-z0-9]/gi, '_')}_${new Date().toLocaleDateString('es-CO').replace(/\//g, '-')}.xlsx`);
  };

  // Calcular estadísticas generales
  const currentStats = filterConcesionario === 'todos' ? stats : stats.filter(s => s.concesionario_id.toString() === filterConcesionario);
  
  const totalStats = currentStats.reduce((acc, curr) => {
    acc.no_registrados += curr.no_registrados;
    acc.registrados += curr.registrados;
    acc.cobrados += curr.cobrados;
    acc.pagados += curr.pagados;
    acc.recaudados += curr.recaudados || 0;
    return acc;
  }, { no_registrados: 0, registrados: 0, cobrados: 0, pagados: 0, recaudados: 0 });

  // Pagination logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentTramites = tramites.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(tramites.length / itemsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  return (
    <div className="app-layout">
      <Sidebar />
      <div className="main-content">
        <Navbar title="🏛️ Gestión de Impuestos" />
        <div className="page-content">
          {error && <div className="error-banner">{error}</div>}
          
          <div className="page-header">
            <div className="header-title">
              <h2>Listado General de Impuestos</h2>
              <p className="subtitle">Gestión de trámites (mayores a 125cc) para registro y cobro</p>
            </div>
            <button onClick={() => { fetchStats(); loadTramites(); }} className="btn-secondary">
              🔄 Actualizar Datos
            </button>
          </div>

          {/* Información General Stats */}
          {!loadingStats && (
            <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(4, 1fr)', marginBottom: '20px' }}>
              <div className="stat-card" style={{ borderLeft: '4px solid #f43f5e' }}>
                <div className="stat-details" style={{ width: '100%', textAlign: 'center' }}>
                  <span className="stat-label" style={{ color: '#64748b' }}>No Registrados</span>
                  <div style={{ color: '#f43f5e', fontWeight: 'bold', fontSize: '24px' }}>{totalStats.no_registrados}</div>
                </div>
              </div>
              <div className="stat-card" style={{ borderLeft: '4px solid #f59e0b' }}>
                <div className="stat-details" style={{ width: '100%', textAlign: 'center' }}>
                  <span className="stat-label" style={{ color: '#64748b' }}>Registrados</span>
                  <div style={{ color: '#f59e0b', fontWeight: 'bold', fontSize: '24px' }}>{totalStats.registrados}</div>
                </div>
              </div>
              <div className="stat-card" style={{ borderLeft: '4px solid #3b82f6' }}>
                <div className="stat-details" style={{ width: '100%', textAlign: 'center' }}>
                  <span className="stat-label" style={{ color: '#64748b' }}>Cobrados</span>
                  <div style={{ color: '#3b82f6', fontWeight: 'bold', fontSize: '24px' }}>{totalStats.cobrados}</div>
                </div>
              </div>
              <div className="stat-card" style={{ borderLeft: '4px solid #10b981' }}>
                <div className="stat-details" style={{ width: '100%', textAlign: 'center' }}>
                  <span className="stat-label" style={{ color: '#64748b' }}>Pagados</span>
                  <div style={{ color: '#10b981', fontWeight: 'bold', fontSize: '24px' }}>{totalStats.pagados}</div>
                </div>
              </div>
              <div className="stat-card" style={{ borderLeft: '4px solid #8b5cf6' }}>
                <div className="stat-details" style={{ width: '100%', textAlign: 'center' }}>
                  <span className="stat-label" style={{ color: '#64748b' }}>Recaudados</span>
                  <div style={{ color: '#8b5cf6', fontWeight: 'bold', fontSize: '24px' }}>{totalStats.recaudados}</div>
                </div>
              </div>
            </div>
          )}

          <div className="dashboard-controls" style={{ marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div className="quick-filters" style={{ display: 'flex', gap: '10px' }}>
              <button 
                className={`btn-filter ${filterEstado === 'todos' ? 'active' : ''}`}
                onClick={() => setFilterEstado('todos')}
              >
                Todos
              </button>
              <button 
                className={`btn-filter ${filterEstado === 'no_registrado' ? 'active' : ''}`}
                onClick={() => setFilterEstado('no_registrado')}
                style={filterEstado === 'no_registrado' ? { background: '#ffe4e6', color: '#e11d48', border: '1px solid #f43f5e' } : {}}
              >
                1. Pendiente
              </button>
              <button 
                className={`btn-filter ${filterEstado === 'registrado' ? 'active' : ''}`}
                onClick={() => setFilterEstado('registrado')}
                style={filterEstado === 'registrado' ? { background: '#fef3c7', color: '#b45309', border: '1px solid #f59e0b' } : {}}
              >
                2. Registrado
              </button>
              <button 
                className={`btn-filter ${filterEstado === 'pagado' ? 'active' : ''}`}
                onClick={() => setFilterEstado('pagado')}
                style={filterEstado === 'pagado' ? { background: '#ecfdf5', color: '#047857', border: '1px solid #10b981' } : {}}
              >
                3. Pagado
              </button>
              <button 
                className={`btn-filter ${filterEstado === 'cobrado' ? 'active' : ''}`}
                onClick={() => setFilterEstado('cobrado')}
                style={filterEstado === 'cobrado' ? { background: '#eff6ff', color: '#1d4ed8', border: '1px solid #3b82f6' } : {}}
              >
                4. Cobrado
              </button>
              <button 
                className={`btn-filter ${filterEstado === 'recaudado' ? 'active' : ''}`}
                onClick={() => setFilterEstado('recaudado')}
                style={filterEstado === 'recaudado' ? { background: '#f3e8ff', color: '#7e22ce', border: '1px solid #8b5cf6' } : {}}
              >
                5. Recaudado
              </button>
            </div>

            <div className="dashboard-filters" style={{ display: 'flex', gap: '20px', flexWrap: 'wrap', marginBottom: '20px', padding: '20px', background: '#fff', borderRadius: '12px', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}>
              
              <div className="filter-group" style={{ display: 'flex', flexDirection: 'column', gap: '6px', width: '200px' }}>
                <label style={{ fontSize: '12px', fontWeight: 'bold', color: '#64748b', textTransform: 'uppercase' }}>Concesionario</label>
                <select 
                  className="form-input" 
                  value={filterConcesionario}
                  onChange={(e) => setFilterConcesionario(e.target.value)}
                >
                  <option value="todos">Todos los concesionarios</option>
                  {stats.map(s => (
                    <option key={s.concesionario_id} value={s.concesionario_id}>
                      {s.concesionario_name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="filter-group" style={{ display: 'flex', flexDirection: 'column', gap: '6px', width: '200px' }}>
                <label style={{ fontSize: '12px', fontWeight: 'bold', color: '#64748b', textTransform: 'uppercase' }}>Estado Trámite</label>
                <select 
                  className="form-input" 
                  value={filterEstadoTramite}
                  onChange={(e) => setFilterEstadoTramite(e.target.value)}
                >
                  <option value="todos">Todos los Estados</option>
                  <option value="radicado">📝 Radicado</option>
                  <option value="pre_asignado">🚗 Pre-Asignado</option>
                  <option value="novedad_pendiente_soat">📄 SOAT Pendiente</option>
                  <option value="novedad_soat_cargado">🛡️ SOAT Cargado</option>
                  <option value="novedad_soat_vigente">🛡️ SOAT Vigente</option>
                  <option value="novedad_pendiente_cupl">💳 CUPL Pendiente</option>
                  <option value="novedad_cupl_generado">📄 CUPL Generado</option>
                  <option value="novedad_cupl_pagado">💳 CUPL Pagado</option>
                  <option value="tramite_ejecutado">🎉 Trámite Ejecutado</option>
                  <option value="placa_en_camino">🚚 Placa en Camino</option>
                  <option value="placa_entregada">🏪 Placa Entregada</option>
                  <option value="placa_confirmada">✅ Placa Confirmada</option>
                  <option value="incidencia_placa">⚠️ Incidencia Placa</option>
                  <option value="rechazado">❌ Rechazado</option>
                </select>
              </div>

              <div className="filter-group" style={{ display: 'flex', flexDirection: 'column', gap: '6px', flex: '1 1 200px' }}>
                <label style={{ fontSize: '12px', fontWeight: 'bold', color: '#64748b', textTransform: 'uppercase' }}>Buscar</label>
                <input 
                  type="text" 
                  placeholder="🔍 Placa o ID..." 
                  className="form-input" 
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>

              <div className="filter-group" style={{ display: 'flex', flexDirection: 'column', gap: '6px', width: '150px' }}>
                <label style={{ fontSize: '12px', fontWeight: 'bold', color: '#64748b', textTransform: 'uppercase' }}>Desde</label>
                <input 
                  type="date" 
                  className="form-input" 
                  value={fechaInicio}
                  onChange={(e) => setFechaInicio(e.target.value)}
                />
              </div>
              
              <div className="filter-group" style={{ display: 'flex', flexDirection: 'column', gap: '6px', width: '150px' }}>
                <label style={{ fontSize: '12px', fontWeight: 'bold', color: '#64748b', textTransform: 'uppercase' }}>Hasta</label>
                <input 
                  type="date" 
                  className="form-input" 
                  value={fechaFin}
                  onChange={(e) => setFechaFin(e.target.value)}
                />
              </div>
              
              <div className="filter-group" style={{ display: 'flex', alignItems: 'flex-end', paddingBottom: '2px' }}>
                <button className="btn-secondary" onClick={() => {
                  setSearch(''); setFechaInicio(''); setFechaFin(''); setFilterConcesionario('todos'); setFilterEstadoTramite('todos');
                }}>
                  Limpiar
                </button>
              </div>
            </div>
          </div>

          <div className="dashboard-controls" style={{ marginBottom: '20px', display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>

            <div className="bulk-actions" style={{ display: 'flex', gap: '10px' }}>
              <button 
                className="btn-secondary" 
                onClick={exportToExcel}
                style={{ background: '#10b981', color: 'white', borderColor: '#059669', marginRight: '20px' }}
              >
                📥 Exportar Excel
              </button>

              {canManageImpuestos && (
                <>
                  <span style={{ display: 'flex', alignItems: 'center', fontSize: '13px', color: '#64748b', marginRight: '10px' }}>
                    {selectedTramites.length} seleccionados
                  </span>
                  
                  <button 
                    className="btn-primary" 
                    onClick={() => handleUpdateStatus('registrado')}
                    disabled={selectedTramites.length === 0}
                    style={{ background: '#f59e0b', color: 'white', borderColor: '#d97706' }}
                  >
                    📝 1. Marcar Registrados
                  </button>

                  <button 
                    className="btn-primary" 
                    onClick={() => handleUpdateStatus('pagado')}
                    disabled={selectedTramites.length === 0}
                    style={{ background: '#10b981' }}
                  >
                    💰 2. Marcar Pagados
                  </button>

                  <button 
                    className="btn-primary" 
                    onClick={() => handleUpdateStatus('cobrado')}
                    disabled={selectedTramites.length === 0}
                    style={{ background: '#3b82f6' }}
                  >
                    🧾 3. Marcar Cobrados
                  </button>

                  <button 
                    className="btn-primary" 
                    onClick={() => handleUpdateStatus('recaudado')}
                    disabled={selectedTramites.length === 0}
                    style={{ background: '#8b5cf6' }}
                  >
                    🏦 4. Marcar Recaudados
                  </button>
                </>
              )}
            </div>
          </div>

          {loadingTramites ? (
            <p className="loading">Cargando trámites...</p>
          ) : (
            <div className="table-wrapper">
              <div className="table-responsive">
                <table className="users-table">
                  <thead>
                    <tr>
                      {canManageImpuestos && (
                        <th style={{ width: '40px', textAlign: 'center' }}>
                          <input 
                            type="checkbox" 
                            onChange={handleSelectAll}
                            checked={tramites.length > 0 && selectedTramites.length === tramites.length}
                            style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                          />
                        </th>
                      )}
                      <th>ID / Placa</th>
                      <th>Concesionario</th>
                      <th>Cilindraje</th>
                      <th>Tránsito</th>
                      <th>Estado del Trámite</th>
                      <th>Estado Impuesto</th>
                      <th>Valor Impuesto</th>
                      <th>Fechas de Impuesto</th>
                      {canManageImpuestos && <th>Acciones</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {currentTramites.map(t => (
                      <tr key={t.id} className={selectedTramites.includes(t.id) ? 'selected-row' : ''}>
                        {canManageImpuestos && (
                          <td style={{ textAlign: 'center' }}>
                            <input 
                              type="checkbox"
                              checked={selectedTramites.includes(t.id)}
                              onChange={() => handleSelectTramite(t.id)}
                              style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                            />
                          </td>
                        )}
                        <td>
                          <strong>{t.plate || 'Sin Placa'}</strong>
                          <div style={{ fontSize: '11px', color: '#94a3b8' }}>ID: #{t.id}</div>
                        </td>
                        <td>
                          <span style={{ color: '#475569', fontSize: '13px' }}>{t.concesionario_name || 'N/A'}</span>
                        </td>
                        <td>
                          <span style={{ fontWeight: 'bold', color: '#64748b' }}>{t.cilindraje} cc</span>
                        </td>
                        <td>{t.transito_name || 'N/A'}</td>
                        <td>
                          <span className="plate-badge-sm" style={{ background: '#f8fafc', color: '#475569', border: '1px solid #e2e8f0' }}>
                            {t.estado_tramite}
                          </span>
                        </td>
                        <td>
                          <span style={{
                            padding: '4px 8px',
                            borderRadius: '4px',
                            fontSize: '12px',
                            fontWeight: '600',
                            backgroundColor: 
                            t.impuesto_estado === 'recaudado' ? '#f3e8ff' :
                            t.impuesto_estado === 'pagado' ? '#d1fae5' : 
                            t.impuesto_estado === 'cobrado' ? '#dbeafe' : 
                            t.impuesto_estado === 'registrado' ? '#fef3c7' : '#ffe4e6',
                          color: 
                            t.impuesto_estado === 'recaudado' ? '#7e22ce' :
                            t.impuesto_estado === 'pagado' ? '#065f46' : 
                            t.impuesto_estado === 'cobrado' ? '#1e40af' : 
                            t.impuesto_estado === 'registrado' ? '#92400e' : '#be123c'
                        }}>
                          {t.impuesto_estado.replace('_', ' ').toUpperCase()}
                          </span>
                        </td>
                        <td>
                          <span style={{ fontWeight: 'bold', color: t.impuesto_valor && Number(t.impuesto_valor) > 0 ? '#059669' : '#64748b', fontSize: '13px' }}>
                            $ {Number(t.impuesto_valor || 0).toLocaleString('es-CO')}
                          </span>
                        </td>
                        <td style={{ fontSize: '12px' }}>
                          {t.impuesto_fecha_registro && (
                            <div><strong style={{ color: '#f59e0b' }}>Reg:</strong> {new Date(t.impuesto_fecha_registro).toLocaleDateString()}</div>
                          )}
                          {t.impuesto_fecha_cobro && (
                            <div><strong style={{ color: '#3b82f6' }}>Cob:</strong> {new Date(t.impuesto_fecha_cobro).toLocaleDateString()}</div>
                          )}
                          {t.impuesto_fecha_pago && (
                            <div><strong style={{ color: '#10b981' }}>Pag:</strong> {new Date(t.impuesto_fecha_pago).toLocaleDateString()}</div>
                          )}
                          {t.impuesto_fecha_recaudo && (
                            <div><strong style={{ color: '#8b5cf6' }}>Rec:</strong> {new Date(t.impuesto_fecha_recaudo).toLocaleDateString()}</div>
                          )}
                        </td>
                        {canManageImpuestos && (
                          <td>
                            <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
                              {/* 3. Pagado: Muestra los 2 PDFs y el botón Editar Pago */}
                              {t.impuesto_estado === 'pagado' && (
                                <>
                                  {t.liquidacion_impuesto_url && (
                                    <a
                                      href={`${API_URL}${t.liquidacion_impuesto_url}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="btn-secondary"
                                      style={{ padding: '4px 8px', fontSize: '11px', textDecoration: 'none', background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe' }}
                                      title="Ver Liquidación PDF"
                                    >
                                      📄 Liquidación
                                    </a>
                                  )}
                                  {t.soporte_impuesto_url && (
                                    <a
                                      href={`${API_URL}${t.soporte_impuesto_url}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="btn-secondary"
                                      style={{ padding: '4px 8px', fontSize: '11px', textDecoration: 'none', background: '#f0fdf4', color: '#15803d', border: '1px solid #bbf7d0' }}
                                      title="Ver Soporte de Pago PDF"
                                    >
                                      💳 Soporte
                                    </a>
                                  )}
                                  <button
                                    className="btn-primary"
                                    style={{ padding: '4px 8px', fontSize: '12px', background: '#10b981' }}
                                    onClick={() => {
                                      setTramiteToPay(t);
                                      setIsPaymentModalOpen(true);
                                    }}
                                  >
                                    📝 Editar Pago
                                  </button>
                                </>
                              )}

                              {/* 2. Registrado: donde debería aparecer el botón Pagar */}
                              {(t.impuesto_estado === 'registrado' || t.impuesto_estado === 'no_registrado') && (
                                <button
                                  className="btn-primary"
                                  style={{ padding: '4px 8px', fontSize: '12px', background: '#3b82f6' }}
                                  onClick={() => {
                                    setTramiteToPay(t);
                                    setIsPaymentModalOpen(true);
                                  }}
                                >
                                  💸 Pagar
                                </button>
                              )}

                              {/* 4. Cobrado: botones Recaudar y Devolver (eliminar a pagado) */}
                              {t.impuesto_estado === 'cobrado' && (
                                <>
                                  <button
                                    className="btn-primary"
                                    style={{ padding: '4px 8px', fontSize: '12px', background: '#8b5cf6' }}
                                    onClick={async () => {
                                      if (window.confirm(`¿Marcar el trámite #${t.id} como RECAUDADO?`)) {
                                        try {
                                          setLoadingTramites(true);
                                          await axios.put('/api/impuestos/update-status', {
                                            tramite_ids: [t.id],
                                            nuevo_estado: 'recaudado'
                                          });
                                          await fetchStats();
                                          loadTramites();
                                        } catch (err) {
                                          alert('Error al recaudar');
                                          setLoadingTramites(false);
                                        }
                                      }
                                    }}
                                  >
                                    🏦 Recaudar
                                  </button>
                                  <button
                                    className="btn-secondary"
                                    style={{ padding: '4px 8px', fontSize: '12px', background: '#fee2e2', color: '#991b1b', border: '1px solid #fca5a5' }}
                                    title="Devolver a estado Pagado"
                                    onClick={async () => {
                                      if (window.confirm(`¿Devolver el trámite #${t.id} al estado PAGADO?`)) {
                                        try {
                                          setLoadingTramites(true);
                                          await axios.put('/api/impuestos/update-status', {
                                            tramite_ids: [t.id],
                                            nuevo_estado: 'pagado'
                                          });
                                          await fetchStats();
                                          loadTramites();
                                        } catch (err) {
                                          alert('Error al devolver estado');
                                          setLoadingTramites(false);
                                        }
                                      }
                                    }}
                                  >
                                    ↩️ Devolver a Pagado
                                  </button>
                                </>
                              )}

                              {/* 5. Recaudado: botón de devolver (eliminar a cobrado) */}
                              {t.impuesto_estado === 'recaudado' && (
                                <button
                                  className="btn-secondary"
                                  style={{ padding: '4px 8px', fontSize: '12px', background: '#fee2e2', color: '#991b1b', border: '1px solid #fca5a5' }}
                                  title="Devolver a estado Cobrado"
                                  onClick={async () => {
                                    if (window.confirm(`¿Devolver el trámite #${t.id} al estado COBRADO?`)) {
                                      try {
                                        setLoadingTramites(true);
                                        await axios.put('/api/impuestos/update-status', {
                                          tramite_ids: [t.id],
                                          nuevo_estado: 'cobrado'
                                        });
                                        await fetchStats();
                                        loadTramites();
                                      } catch (err) {
                                        alert('Error al devolver estado');
                                        setLoadingTramites(false);
                                      }
                                    }
                                  }}
                                >
                                  ↩️ Devolver a Cobrado
                                </button>
                              )}

                              {/* Opción adicional para Superadmin de eliminar archivos físicos si los hay */}
                              {isSuperAdmin && t.liquidacion_impuesto_url && t.impuesto_estado !== 'pagado' && (
                                <button
                                  type="button"
                                  onClick={() => handleDeleteSoporte(t.id, 'LIQUIDACION_IMPUESTO', 'Liquidación')}
                                  style={{ background: '#fef2f2', border: '1px solid #fca5a5', color: '#dc2626', borderRadius: '4px', padding: '3px 6px', fontSize: '11px', cursor: 'pointer' }}
                                  title="Eliminar Liquidación (Superadmin)"
                                >
                                  🗑️ Liq
                                </button>
                              )}
                              {isSuperAdmin && t.soporte_impuesto_url && t.impuesto_estado !== 'pagado' && (
                                <button
                                  type="button"
                                  onClick={() => handleDeleteSoporte(t.id, 'SOPORTE_PAGO_IMPUESTO', 'Soporte de Pago')}
                                  style={{ background: '#fef2f2', border: '1px solid #fca5a5', color: '#dc2626', borderRadius: '4px', padding: '3px 6px', fontSize: '11px', cursor: 'pointer' }}
                                  title="Eliminar Soporte de Pago (Superadmin)"
                                >
                                  🗑️ Sop
                                </button>
                              )}
                            </div>
                          </td>
                        )}
                      </tr>
                    ))}
                    
                    {tramites.length === 0 && (
                      <tr>
                        <td colSpan="8" className="no-data" style={{ textAlign: 'center', padding: '20px' }}>
                          No hay vehículos &gt; 125cc con los filtros actuales.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              
              {/* Pagination Controls */}
              {tramites.length > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '15px', padding: '10px 0' }}>
                  <div style={{ fontSize: '13px', color: '#64748b' }}>
                    Mostrando {indexOfFirstItem + 1} a {Math.min(indexOfLastItem, tramites.length)} de {tramites.length} registros
                  </div>
                  <div style={{ display: 'flex', gap: '5px' }}>
                    <button 
                      onClick={() => paginate(currentPage - 1)} 
                      disabled={currentPage === 1}
                      style={{ padding: '5px 10px', background: currentPage === 1 ? '#e2e8f0' : '#fff', border: '1px solid #cbd5e1', borderRadius: '4px', cursor: currentPage === 1 ? 'not-allowed' : 'pointer' }}
                    >
                      Anterior
                    </button>
                    
                    <span style={{ padding: '5px 10px', fontSize: '14px', fontWeight: 'bold' }}>
                      Página {currentPage} de {totalPages}
                    </span>
                    
                    <button 
                      onClick={() => paginate(currentPage + 1)} 
                      disabled={currentPage === totalPages}
                      style={{ padding: '5px 10px', background: currentPage === totalPages ? '#e2e8f0' : '#fff', border: '1px solid #cbd5e1', borderRadius: '4px', cursor: currentPage === totalPages ? 'not-allowed' : 'pointer' }}
                    >
                      Siguiente
                    </button>
                    
                    <select 
                      value={itemsPerPage} 
                      onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}
                      style={{ marginLeft: '10px', padding: '5px', border: '1px solid #cbd5e1', borderRadius: '4px' }}
                    >
                      <option value={20}>20 por pág.</option>
                      <option value={50}>50 por pág.</option>
                      <option value={100}>100 por pág.</option>
                    </select>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      <CargarPagoImpuestoModal 
        isOpen={isPaymentModalOpen}
        onClose={() => {
          setIsPaymentModalOpen(false);
          setTramiteToPay(null);
        }}
        tramite={tramiteToPay}
        onPaymentSuccess={(data) => {
          alert(`Pago registrado exitosamente. Valor registrado: $${data.final_value || 'N/A'}`);
          fetchStats();
          loadTramites();
        }}
      />
    </div>
  );
};

export default Impuestos;
